// ShareRouter.jsx - Routes between welcome screen, team view, and full schedule
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import WelcomeScreen from './WelcomeScreen';
import TeamScheduleView from './TeamScheduleView';
import ShareView from './ShareView';

const ShareRouter = () => {
  const { clubId, dateOrToday, team } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareData, setShareData] = useState(null);
  const [teamsPlayingToday, setTeamsPlayingToday] = useState([]);

  // Detect if this is actually a legacy share link (random ID format)
  // Legacy IDs are long strings like "1771240412653-mtf83eike"
  // New club IDs are short like "23PKT8" or "SG5LKT"
  useEffect(() => {
    const isLegacyShareId = clubId && (
      clubId.length > 15 || // Legacy IDs are typically much longer
      clubId.includes('-') && clubId.split('-').length > 1 && clubId.split('-')[0].length > 10 // Has long numeric prefix
    );
    
    if (isLegacyShareId) {
      // This is actually a legacy share link, redirect to old ShareView
      console.log('🔀 Detected legacy share link, redirecting to ShareView');
      window.location.href = `/share/${clubId}${dateOrToday ? `/${dateOrToday}` : ''}`;
      return;
    }
  }, [clubId, dateOrToday]);

  // Determine actual date
  const getActualDate = () => {
    if (dateOrToday === 'today') {
      const now = new Date();
      return now.toISOString().split('T')[0]; // YYYY-MM-DD
    }
    return dateOrToday;
  };

  const actualDate = getActualDate();

  // Load share data and determine which teams are playing
  useEffect(() => {
    const loadShareData = async () => {
      try {
        setLoading(true);
        
        // Import Firebase
        const { doc, getDoc, collection, query, where, getDocs, db } = await import('firebase/firestore').then(async (firestore) => {
          const firebase = await import('../utils/firebase');
          return { ...firestore, db: firebase.db };
        });

        // Get club data
        const clubDoc = await getDoc(doc(db, 'clubs', clubId));
        if (!clubDoc.exists()) {
          throw new Error('Club not found');
        }

        const clubData = clubDoc.data();
        
        // Get facility data (where satellite config actually lives)
        const facilitiesRef = collection(db, 'clubs', clubId, 'facilities');
        const facilitiesSnapshot = await getDocs(facilitiesRef);
        
        let satelliteConfig = null;
        let pitches = [];
        
        // Get first facility (most clubs have one primary facility)
        if (!facilitiesSnapshot.empty) {
          const facilityDoc = facilitiesSnapshot.docs[0];
          const facilityData = facilityDoc.data();
          
          satelliteConfig = facilityData.satelliteConfig || {};
          satelliteConfig.pitchBoundaries = facilityData.pitches || [];
          
          // Use pitches from facility
          pitches = facilityData.pitches || [];
        }
        
        // Fallback to club-level data if no facility data
        if (!satelliteConfig) {
          satelliteConfig = clubData.satelliteConfig;
          pitches = clubData.pitches || satelliteConfig?.pitchBoundaries || [];
        }
        
        // Load pitch configurations
        const settingsRef = doc(db, 'clubs', clubId, 'settings', 'general');
        const settingsDoc = await getDoc(settingsRef);
        let pitchConfigs = {};
        let branding = {};
        
        if (settingsDoc.exists()) {
          pitchConfigs = settingsDoc.data().pitchSectionConfigs || {};
          branding = settingsDoc.data().branding || {};
          
          // Also get pitch names and reference points if available
          const pitchNames = settingsDoc.data().pitchNames || {};
          const pitchReferencePoints = settingsDoc.data().pitchReferencePoints || {};
          
          if (pitches.length === 0) {
            // Try to load from settings
            Object.keys(pitchNames).forEach(pitchKey => {
              const pitchNumber = pitchKey.replace(/\D/g, '');
              if (pitchNumber) {
                pitches.push({ pitchNumber });
              }
            });
          }
          
          // Store reference points to be added to shareData later
          window._tempPitchReferencePoints = pitchReferencePoints;
        }
        
        console.log('🏟️ Club data:', {
          hasSatelliteConfig: !!satelliteConfig,
          satelliteImageUrl: satelliteConfig?.imageUrl,
          pitchBoundariesCount: satelliteConfig?.pitchBoundaries?.length || 0,
          pitchConfigsKeys: Object.keys(pitchConfigs),
          pitchesCount: pitches.length
        });

        // Load allocations for this date from all pitches
        const allocationPromises = pitches.map(async (pitch) => {
          let pitchNumber = pitch.pitchNumber;
          
          if (!pitchNumber && pitch.id) {
            pitchNumber = pitch.id.replace(/\D/g, '');
          }
          
          if (!pitchNumber && pitch.name) {
            const nameMatch = pitch.name.match(/\d+/);
            pitchNumber = nameMatch ? nameMatch[0] : null;
          }
          
          if (!pitchNumber) return {};
          
          const pitchIdFormat = `pitch-${pitchNumber}`;
          
          try {
            const allocationsRef = collection(db, 'clubs', clubId, 'allocations', pitchIdFormat, 'items');
            const allocationsQ = query(allocationsRef, where('date', '==', actualDate));
            const allocationsSnapshot = await getDocs(allocationsQ);
            
            const pitchAllocations = {};
            allocationsSnapshot.forEach((docSnapshot) => {
              const data = docSnapshot.data();
              pitchAllocations[docSnapshot.id] = {
                ...data,
                pitchId: pitchIdFormat,
                pitchNumber,
                type: data.allocationType || data.type || 'training'
              };
            });
            
            return pitchAllocations;
          } catch (err) {
            console.error(`Error querying allocations for ${pitchIdFormat}:`, err);
            return {};
          }
        });

        const results = await Promise.all(allocationPromises);
        const allocations = results.reduce((acc, curr) => ({ ...acc, ...curr }), {});

        // Extract unique teams from allocations
        // Handle two structures:
        // 1. NEW: sections array with objects: sections: [{section: "A", team: "U8s"}]
        // 2. ACTUAL: team at top level: {team: "Senior Mens", sections: ["H"]}
        const teamsSet = new Set();
        Object.values(allocations).forEach(allocation => {
          // Check if team is at top level (your actual structure)
          if (allocation.team) {
            teamsSet.add(allocation.team);
          } 
          // Also check if team is in teamName field
          else if (allocation.teamName) {
            teamsSet.add(allocation.teamName);
          }
          // Check if sections is array of objects with team property (future structure)
          else if (allocation.sections && Array.isArray(allocation.sections)) {
            allocation.sections.forEach(section => {
              if (typeof section === 'object' && section.team) {
                teamsSet.add(section.team);
              }
            });
          }
        });

        const teams = Array.from(teamsSet).sort();

        // Look up legacy share link for this club+date so "View All Teams" can open ShareView
        let legacyShareId = null;
        try {
          const sharedRef = collection(db, 'sharedAllocations');
          const sharedQ = query(sharedRef, where('clubId', '==', clubId), where('date', '==', actualDate));
          const sharedSnap = await getDocs(sharedQ);
          if (!sharedSnap.empty) {
            legacyShareId = sharedSnap.docs[0].id;
          }
        } catch (err) {
          // Not critical
        }

        setShareData({
          clubId,
          clubName: clubData.name || 'Club',
          clubLogo: clubData.logo,
          date: actualDate,
          allocations,
          pitches,
          clubData,
          satelliteConfig,
          pitchConfigs,
          branding,
          pitchReferencePoints: window._tempPitchReferencePoints || {},
          legacyShareId,
          facilityId: facilitiesSnapshot.empty ? null : facilitiesSnapshot.docs[0].id
        });
        
        // Clean up temp variable
        delete window._tempPitchReferencePoints;

        setTeamsPlayingToday(teams);
        setLoading(false);

      } catch (err) {
        console.error('Error loading share data:', err);
        
        // If club not found, this might be a legacy share link
        if (err.message === 'Club not found') {
          setError('This appears to be an old share link format. Please request a new share link from your club coordinator.');
        } else {
          setError(err.message || 'Failed to load data');
        }
        setLoading(false);
      }
    };

    loadShareData();
  }, [clubId, actualDate]);

  // Handle team selection from welcome screen
  const handleTeamSelect = (selectedTeam) => {
    if (selectedTeam === 'all') {
      navigate(`/share/${clubId}/${dateOrToday}/all-teams`);
    } else {
      navigate(`/share/${clubId}/${dateOrToday}/${encodeURIComponent(selectedTeam)}`);
    }
  };

  // Handle "View All Teams" from team view
  const handleViewAllTeams = () => {
    navigate(`/share/${clubId}/${dateOrToday}/all-teams`);
  };

  // Handle back to team selection
  const handleBackToSelection = () => {
    navigate(`/share/${clubId}/${dateOrToday}`);
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#243665'
      }}>
        <div style={{
          textAlign: 'center'
        }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #e5e7eb',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            margin: '0 auto 16px',
            animation: 'spin 1s linear infinite'
          }} />
          <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          <p style={{ color: '#6b7280', fontSize: '16px' }}>Loading schedule...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#243665',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '32px',
          borderRadius: '12px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          <div style={{
            fontSize: '48px',
            marginBottom: '16px'
          }}>⚠️</div>
          <h2 style={{
            fontSize: '20px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '8px'
          }}>
            Unable to Load Schedule
          </h2>
          <p style={{
            color: '#6b7280',
            fontSize: '14px'
          }}>
            {error}
          </p>
        </div>
      </div>
    );
  }

  // No allocations for this date
  if (teamsPlayingToday.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#243665',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '40px',
          borderRadius: '16px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
          textAlign: 'center',
          maxWidth: '400px'
        }}>
          {shareData?.clubLogo && (
            <img 
              src={shareData.clubLogo} 
              alt={shareData.clubName}
              style={{
                width: '80px',
                height: '80px',
                objectFit: 'contain',
                margin: '0 auto 24px'
              }}
            />
          )}
          <h2 style={{
            fontSize: '24px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '12px'
          }}>
            No Matches Scheduled
          </h2>
          <p style={{
            color: '#6b7280',
            fontSize: '16px',
            marginBottom: '8px'
          }}>
            {shareData?.clubName}
          </p>
          <p style={{
            color: '#9ca3af',
            fontSize: '14px'
          }}>
            {new Date(actualDate).toLocaleDateString('en-AU', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </p>
        </div>
      </div>
    );
  }

  // Route to appropriate view based on URL
  if (!team) {
    // Show welcome screen for team selection
    return (
      <WelcomeScreen
        clubName={shareData.clubName}
        clubLogo={shareData.clubLogo}
        date={actualDate}
        teams={teamsPlayingToday}
        onTeamSelect={handleTeamSelect}
      />
    );
  }

  if (team === 'all-teams') {
    // Redirect to the legacy ShareView if we have a share link
    if (shareData?.legacyShareId) {
      window.location.href = `/share/${shareData.legacyShareId}`;
      return null;
    }
    // Fallback: no legacy share link exists yet
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: '16px', padding: '20px', textAlign: 'center' }}>
        <div style={{ fontSize: '48px' }}>📅</div>
        <div style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937' }}>Full Schedule Not Available</div>
        <div style={{ fontSize: '15px', color: '#6b7280' }}>A full schedule hasn't been shared for this date yet. Ask your club coordinator to share it.</div>
        <button onClick={handleBackToSelection} style={{ marginTop: '8px', padding: '10px 24px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer' }}>
          ← Back
        </button>
      </div>
    );
  }

  // Show team-specific view
  return (
    <TeamScheduleView
      clubName={shareData.clubName}
      clubLogo={shareData.clubLogo}
      teamName={team}
      date={actualDate}
      allocations={shareData.allocations}
      pitches={shareData.pitches}
      satelliteConfig={shareData.satelliteConfig}
      pitchConfigs={shareData.pitchConfigs}
      branding={shareData.branding}
      pitchReferencePoints={shareData.pitchReferencePoints || {}}
      onViewAllTeams={handleViewAllTeams}
      onBack={handleBackToSelection}
    />
  );
};

export default ShareRouter;
